import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link, useParams, useNavigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import './BlogPage.css';

// Simple front matter parser
const parseFrontMatter = (content) => {
  const frontMatterRegex = /^---\n([\s\S]*?)\n---\n([\s\S]*)$/;
  const match = content.match(frontMatterRegex);

  if (!match) {
    return { metadata: {}, content };
  }

  const frontMatter = match[1];
  const markdown = match[2];

  // Parse YAML-like front matter
  const metadata = {};
  frontMatter.split('\n').forEach(line => {
    const colonIndex = line.indexOf(':');
    if (colonIndex > 0) {
      const key = line.slice(0, colonIndex).trim();
      let value = line.slice(colonIndex + 1).trim();

      // Handle arrays (tags: [a, b, c] or tags: a, b, c)
      if (value.startsWith('[') && value.endsWith(']')) {
        value = value.slice(1, -1).split(',').map(v => v.trim().replace(/['"]/g, ''));
      } else if (key === 'tags' && value.includes(',')) {
        value = value.split(',').map(v => v.trim());
      } else {
        // Remove quotes
        value = value.replace(/^['"]|['"]$/g, '');
      }

      metadata[key] = value;
    }
  });

  return { metadata, content: markdown };
};

const BlogPostPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetch(`/blog/posts/${slug}.md`)
      .then(res => {
        if (!res.ok) throw new Error('Post not found');
        return res.text();
      })
      .then(text => {
        const { metadata, content } = parseFrontMatter(text);
        setPost({ ...metadata, content });
        setLoading(false);
      })
      .catch(err => {
        console.error('Failed to load post:', err);
        setError(err.message);
        setLoading(false);
      });
  }, [slug]);

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="blog-page">
        <div className="blog-bg">
          <div className="bg-gradient" />
        </div>
        <div className="blog-loading-full">Loading...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="blog-page">
        <div className="blog-bg">
          <div className="bg-gradient" />
        </div>
        <div className="blog-error">
          <h1>Post Not Found</h1>
          <p>Sorry, we couldn't find the post you're looking for.</p>
          <button onClick={() => navigate('/blog')} className="back-button">
            ← Back to Blog
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="blog-page blog-post-page">
      {/* Background */}
      <div className="blog-bg">
        <div className="bg-gradient" />
        <div className="grid-pattern" />
        <div className="glow-orb orb-1" />
        <div className="glow-orb orb-2" />
      </div>

      {/* Post Header */}
      <section className="blog-post-hero">
        <div className="section-container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <Link to="/blog" className="back-to-blog">← Back to Blog</Link>
            <div className="post-header-meta">
              {post.date && (
                <time dateTime={post.date}>{formatDate(post.date)}</time>
              )}
              {post.author && (
                <span className="post-author">by {post.author}</span>
              )}
            </div>
            <h1>{post.title || 'Untitled'}</h1>
            {post.description && (
              <p className="post-description">{post.description}</p>
            )}
            {post.tags && post.tags.length > 0 && (
              <div className="post-header-tags">
                {post.tags.map(tag => (
                  <span key={tag} className="post-tag">{tag}</span>
                ))}
              </div>
            )}
          </motion.div>
        </div>
      </section>

      {/* Post Content */}
      <section className="blog-post-content">
        <div className="section-container">
          <motion.article
            className="post-body"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.6 }}
          >
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              components={{
                // Custom component renderers for better styling
                // Skip H1 - title is already displayed in the hero section
                h1: () => null,
                h2: ({node, ...props}) => <h2 className="post-h2" {...props} />,
                h3: ({node, ...props}) => <h3 className="post-h3" {...props} />,
                a: ({node, ...props}) => <a className="post-link" target="_blank" rel="noopener noreferrer" {...props} />,
                code: ({node, inline, ...props}) =>
                  inline ? (
                    <code className="inline-code" {...props} />
                  ) : (
                    <code className="code-block" {...props} />
                  ),
                pre: ({node, ...props}) => <pre className="pre-block" {...props} />,
                blockquote: ({node, ...props}) => <blockquote className="post-blockquote" {...props} />,
                img: ({node, ...props}) => <img className="post-image" loading="lazy" {...props} />,
              }}
            >
              {post.content}
            </ReactMarkdown>
          </motion.article>
        </div>
      </section>

      {/* Footer */}
      <div className="section-container">
        <motion.div
          className="post-footer"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
        >
          <Link to="/blog" className="back-link">← Back to Blog</Link>
        </motion.div>
      </div>
    </div>
  );
};

export default BlogPostPage;
